import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import { Helmet } from 'react-helmet';
import dayjs from 'dayjs';

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, CustomBox, GridChild, GridContainer, Title } from 'common';
import { Event, useEvent } from 'api/event';
import { EventCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext'
import { eventSinglePageLabels } from '../../locales/translations'

import { EventImagesCarousel } from './EventImagesCarousel'

export const SingleEventPage = () => {
    const { language } = useLanguage();

    const { id } = useParams();
    const { onGetOneEvent, onGetAllEvent } = useEvent();

    const [eventDetails, setEventDetails] = useState<Event | null>(null);
    const [latestEvent, setLatestEvent] = useState<Event[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [loadingLatestEvent, setLoadingLatestEvent] = useState(true);
    const [error, setError] = useState<string>('');

    const fetchEventDetails = useCallback(async () => {
        try {
            const result = await onGetOneEvent(Number(id));
            if (result.success) {
                setEventDetails(result.data);
            } else {
                setError('Не удалось загрузить детали ивента.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе деталей ивента.');
        } finally {
            setLoadingDetails(false);
        }
    }, [id, onGetOneEvent]);

    const fetchLatestEvent = useCallback(async () => {
        try {
            const result = await onGetAllEvent();
            if (result.success) {
                const sortedEvent = result.data.sort((a: Event, b: Event) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setLatestEvent(sortedEvent);
            } else {
                setError('Произошла ошибка при загрузке списка ивентов.');
            }
        } catch (error) {
            setError('Произошла ошибка при запросе списка ивентов.');
        } finally {
            setLoadingLatestEvent(false);
        }
    }, [onGetAllEvent]);

    useEffect(() => {
        fetchEventDetails();
        fetchLatestEvent();
    }, [fetchEventDetails, fetchLatestEvent]);

    if (loadingDetails && loadingLatestEvent)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );

    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    {eventDetails && (
                        <CustomBox title={eventDetails[`title_${language}`]}>
                            <Helmet>
                                <title>{eventDetails[`title_${language}`]}</title>
                                <meta name="description" content={eventDetails[`description_${language}`]} />
                            </Helmet>
                            {eventDetails.images && eventDetails.images.length > 0 && eventDetails.images[0] ? (
                                <EventImagesCarousel eventItem={eventDetails} />
                            ) : (<ImageNotSupportedIcon
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '5rem'
                                }}
                            />)}
                            <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                {eventDetails[`title_${language}`]}
                            </Title>
                            <Title gutterBottom variant="body2" color="text.secondary">
                                <div dangerouslySetInnerHTML={{
                                    __html: eventDetails[`description_${language}`]
                                }} />
                            </Title>
                            <Title variant="body2" color="text.primary">
                                {dayjs(eventDetails.created_at).format('DD.MM.YYYY')}
                            </Title>
                        </CustomBox>
                    )}
                </GridChild>

                <GridChild md={4}>
                    {!loadingLatestEvent && (
                        <CustomBox title={eventSinglePageLabels.latestEvents[language]}>
                            {latestEvent.slice(0, 3).map((eventItem) => (
                                <EventCard key={eventItem.id} eventsItem={eventItem} />
                            ))}

                            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'start' }}>
                                <Button variant="contained" color="error" href="/events">
                                    {eventSinglePageLabels.allEventsButton[language]}
                                </Button>
                            </Box>
                        </CustomBox>
                    )}
                </GridChild>
            </GridContainer>
        </Box>
    );
};
